import {gql} from '@apollo/client';
import {useCallback, useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {createAgentFoundry as createAgentFoundryApi} from 'llm/chat-library/foundries/AgentFoundriesDAO';

import {
  AgentFoundriesQuery as AgentFoundriesQueryData,
  AgentFoundriesQuery_agentFoundries_AgentFoundryNode as AgentFoundrySummary,
} from './__generated/AgentFoundriesQuery';

export type {AgentFoundrySummary};

const AGENT_FOUNDRIES_QUERY = gql`
  query AgentFoundriesQuery @engComponent(name: LLM) @skipAudit {
    agentFoundries {
      id
      name
      description
      createdAt
      creatorName
    }
  }
`;

interface UseAgentFoundriesResult {
  agentFoundries: Array<AgentFoundrySummary>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  createAgentFoundry: (name: string, description: string) => Promise<void>;
}

export default function useAgentFoundries(): UseAgentFoundriesResult {
  const queryResult = useBenchlingQuery<AgentFoundriesQueryData>(AGENT_FOUNDRIES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const agentFoundries: Array<AgentFoundrySummary> = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.agentFoundries;
  }, [queryResult]);

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const isError = queryResult.type === QueryResultType.ERROR;

  const createAgentFoundry = useCallback(
    async (name: string, description: string) => {
      await createAgentFoundryApi(name, description);
      await queryResult.refetch();
    },
    [queryResult]
  );

  return {agentFoundries, isLoading, isError, refetch: queryResult.refetch, createAgentFoundry};
}
